import { type ChatRequest, ChatStreamChunkSchema, type Message } from '@chatbot/shared'
import { useCallback, useState } from 'react'
import { STREAMING_URL } from '../config'
import { useAuth } from '../contexts/AuthContext'

export interface UseChatOptions {
  assistantId: string
  onError?: (error: string) => void
}

export interface UseChatReturn {
  messages: Message[]
  isLoading: boolean
  threadId: string | null
  sendMessage: (content: string) => Promise<void>
  clearMessages: () => void
}

export const useChat = ({ assistantId, onError }: UseChatOptions): UseChatReturn => {
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [threadId, setThreadId] = useState<string | null>(null)
  const { token } = useAuth()

  const sendMessage = useCallback(
    async (content: string) => {
      if (isLoading) return

      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        timestamp: Date.now(),
      }

      setMessages((prev) => [...prev, userMessage])
      setIsLoading(true)
      let assistantMessageId: string | null = null

      try {
        const request: ChatRequest = {
          message: content,
          assistantId,
          threadId: threadId || undefined,
        }

        const response = await fetch(`${STREAMING_URL}/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(request),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body')
        }

        const decoder = new TextDecoder()
        assistantMessageId = crypto.randomUUID()
        let assistantMessage: Message = {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: Date.now(),
        }

        setMessages((prev) => [...prev, assistantMessage])

        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            const chunk = decoder.decode(value)
            const lines = chunk.split('\n')

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = JSON.parse(line.slice(6))
                const validatedChunk = ChatStreamChunkSchema.parse(data)

                if (validatedChunk.threadId && !threadId) {
                  setThreadId(validatedChunk.threadId)
                }

                if (validatedChunk.type === 'content' && validatedChunk.content) {
                  assistantMessage = {
                    ...assistantMessage,
                    content: assistantMessage.content + validatedChunk.content,
                  }

                  setMessages((prev) =>
                    prev.map((msg) => (msg.id === assistantMessage.id ? assistantMessage : msg))
                  )
                } else if (validatedChunk.type === 'error') {
                  throw new Error(validatedChunk.error || 'Unknown error')
                } else if (validatedChunk.type === 'done') {
                  break
                }
              }
            }
          }
        } finally {
          reader.releaseLock()
        }
      } catch (error) {
        console.error('Chat error:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        onError?.(errorMessage)

        if (assistantMessageId) {
          setMessages((prev) => prev.filter((m) => m.id !== assistantMessageId))
        }
      } finally {
        setIsLoading(false)
      }
    },
    [assistantId, threadId, isLoading, onError, token]
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    setThreadId(null)
  }, [])

  return {
    messages,
    isLoading,
    threadId,
    sendMessage,
    clearMessages,
  }
}
